import { Button, Checkbox, ListItemText, MenuItem } from "@mui/material";

import { BenefitsPolicyVigencyType, IBenefitsPolicy, IBenefitsPolicyRole } from "../../SetupLife.types";
import { ButtonDelete, Row, SelectForm, Title } from "./BenefitsPolicy.styled";
import { useSetup } from "../../SetupLife.context";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";

interface IProps {
  benefitsPolicy: IBenefitsPolicy;
  handleChange: (payload: Partial<IBenefitsPolicy>) => void;
}

export function BenefitsPolicyRole({ benefitsPolicy, handleChange }: IProps) {
  const { setup } = useSetup();
  const { benefitsPolicyByRole } = benefitsPolicy;

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", setup.rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${setup.rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  if (!isFetched && isFetching) return <div>Loading...</div>;

  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];
  /** Opções possíveis da coluna de Plano. */
  const planOptions: string[] = [];

  /** Função responsável especificamente por receber os valores alterados e formatar corretamente para handleChange. */
  function handleUpdateRole(payload: Partial<IBenefitsPolicyRole>, index: number) {
    const updatedPayload = benefitsPolicyByRole.map((b, i) => i === index ? { ...b, ...payload } : b);

    handleChange({ ...benefitsPolicy, benefitsPolicyByRole: updatedPayload });
  }

  /** Função responsável por remover campo da array de regras de roles. */
  function handleRemoveRole(index: number) {
    const updatedPayload = benefitsPolicyByRole.filter((_b, i) => i !== index);

    return handleChange({ ...benefitsPolicy, benefitsPolicyByRole: updatedPayload });
  }

  /** Função responsável por adicionar novo campo vazio à array de roles. */
  function handleAddRole() {
    const newRole = { roleName: undefined, planName: undefined, effectiveDate: undefined };

    return handleChange({ ...benefitsPolicy, benefitsPolicyByRole: [...benefitsPolicyByRole, newRole] });
  }

  /** Função para obter opções disponíveis (não desabilitadas) para um índice específico. */
  function getAvailableRoles(index: number) {
    return roleOptions.filter(option =>
      !benefitsPolicyByRole.some((b, i) => i !== index && b.roleName?.includes(option))
    );
  }

  /** Função para manipular seleção de "Selecionar Todos" no SelectForm. */
  function handleSelectAllRoles(index: number) {
    const currentValue = benefitsPolicyByRole[index].roleName ?? [];
    const availableRoles = getAvailableRoles(index);
    const allSelected = availableRoles.every(role => currentValue.includes(role));

    if (allSelected) {
      // Desselecionar todos
      handleUpdateRole({ roleName: [] }, index);
    } else {
      // Selecionar todos os disponíveis
      handleUpdateRole({ roleName: availableRoles }, index);
    }
  }

  return (
    <>
      <Title>Cargos</Title>
      {benefitsPolicyByRole.map((benefit, index) => (
        <Row key={index} style={{ alignItems: "center"}}>
          <SelectForm
            label="Cargo"
            multiple
            size="small"
            value={benefit.roleName ?? []}
            onChange={(e) => {
              const value = e.target.value as string[];

              // Verifica se "SELECT_ALL" foi clicado
              if (value.includes("SELECT_ALL")) {
                handleSelectAllRoles(index);
              } else {
                handleUpdateRole({ roleName: [...value] }, index);
              }
            }}
            renderValue={(selected) => (selected as string[]).join(", ")}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <MenuItem value="SELECT_ALL" dense>
              <Checkbox
                size="small"
                checked={
                  getAvailableRoles(index).length > 0 &&
                  getAvailableRoles(index).every(role => (benefit.roleName ?? []).includes(role))
                }
                indeterminate={
                  (benefit.roleName ?? []).length > 0 &&
                  !getAvailableRoles(index).every(role => (benefit.roleName ?? []).includes(role))
                }
                sx={{ py: 0 }}
              />
              <ListItemText
                primary="Selecionar Todos"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
            {roleOptions.sort().map(option => (
              <MenuItem
                key={option}
                value={option}
                dense
                disabled={benefitsPolicyByRole.some((b, i) => i !== index && b.roleName?.includes(option))}
              >
                <Checkbox
                  size="small"
                  checked={(benefit.roleName ?? []).includes(option)}
                  sx={{ py: 0 }}
                />
                <ListItemText
                  primary={option}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </MenuItem>
            ))}
          </SelectForm>
          <SelectForm
            label="Negociação"
            value={benefit.planName}
            onChange={(e) => handleUpdateRole({ planName: e.target.value as string }, index)}
          >
            {benefitsPolicy.negotiations.map(negotiation => (
              <MenuItem key={negotiation} value={negotiation} disabled={benefitsPolicyByRole.map(b => b.planName).includes(negotiation)}>{negotiation}</MenuItem>
            ))}
          </SelectForm>
          <ButtonDelete color="error" onClick={() => handleRemoveRole(index)} />
        </Row>
      ))}
      <Button onClick={handleAddRole}>+ Adicionar</Button>
    </>
  );
}
